import { Link } from '@tanstack/react-router';
import { ArrowLeft, ChevronDown, RotateCcw, Save, ShieldAlert, Wand2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { AdminLayout } from '@/app/shell/admin-layout';
import {
  type PlanPatch,
  type PreviewResult,
  useAdminPlan,
  usePlanApply,
  usePlanHistory,
  usePlanPreview,
  usePlanRevert,
} from '@/shared/api/admin-plans';
import { cn } from '@/shared/lib/cn';

/**
 * Plan editor — the 7-safeguards flow on the frontend:
 *   1. Reason ≥ 10 chars (validated locally + by server)
 *   2. Impact preview — POST /preview must succeed before Apply enables
 *   3. Type 'CONFIRM' literal in confirmation field
 *   4. Shrink warnings — surfaced from preview.warnings array
 *   5. (Soft enforcement is backend-only — no FE concern)
 *   6. Revert from history panel (≤30 days)
 *   7. Cooldown — preview.cooldown.isInCooldown disables Apply with countdown
 */
export function PlanDetailPage({ code }: { code: string }) {
  const { data: plan, isLoading, error } = useAdminPlan(code);
  const { data: history } = usePlanHistory(code);
  const previewMut = usePlanPreview();
  const applyMut = usePlanApply();
  const revertMut = usePlanRevert();

  const [patch, setPatch] = useState<PlanPatch>({});
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [preview, setPreview] = useState<PreviewResult | null>(null);

  const isDirty = Object.keys(patch).length > 0;
  const reasonOk = reason.trim().length >= 10;
  const confirmOk = confirmText === 'CONFIRM';
  const previewOk = preview !== null && !preview.cooldown.isInCooldown;
  const canApply = isDirty && reasonOk && confirmOk && previewOk && !applyMut.isPending;

  // Reset preview state when patch changes.
  function patchField<K extends keyof PlanPatch>(key: K, value: PlanPatch[K]) {
    setPatch((cur) => {
      const next = { ...cur };
      if (value === undefined || value === '') {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
    setPreview(null); // invalidate preview on any edit
    setConfirmText(''); // require re-confirm
  }

  function patchFeature(featureKey: string, on: boolean | null) {
    setPatch((cur) => {
      const features = { ...(cur.features ?? {}) };
      if (on === null) {
        delete features[featureKey];
      } else {
        features[featureKey] = on;
      }
      const next = { ...cur };
      if (Object.keys(features).length === 0) {
        delete next.features;
      } else {
        next.features = features;
      }
      return next;
    });
    setPreview(null);
    setConfirmText('');
  }

  async function runPreview() {
    if (!isDirty) return;
    try {
      const result = await previewMut.mutateAsync({ code, patch });
      setPreview(result);
    } catch {
      // MutationCache toasts; preview stays null
    }
  }

  async function apply() {
    if (!canApply) return;
    try {
      await applyMut.mutateAsync({ code, patch, reason: reason.trim(), confirmText });
      toast.success(`Plan ${code} updated`);
      // Reset form state.
      setPatch({});
      setReason('');
      setConfirmText('');
      setPreview(null);
    } catch {
      /* toast'ed */
    }
  }

  // Projected plan = current merged with patch.  Currently unused on the page; kept for
  // future side-by-side comparison view.
  const projected = useMemo(() => (plan ? { ...plan, ...patch } : null), [plan, patch]);

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="text-sm text-neutral-500">Loading…</div>
      </AdminLayout>
    );
  }
  if (error || !plan) {
    return (
      <AdminLayout>
        <div className="rounded-lg border border-red-900 bg-red-950/30 p-4 text-sm text-red-300">
          Plan not found.
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Link
        to="/plans"
        className="mb-4 inline-flex items-center gap-1 text-sm text-neutral-400 hover:text-neutral-200"
      >
        <ArrowLeft size={14} /> All plans
      </Link>

      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-100">{plan.name}</h1>
          <p className="text-sm text-neutral-400">
            <code className="text-orange-400">{plan.code}</code> · {plan.teamCount} teams on this plan
          </p>
        </div>
      </header>

      {/* Edit form */}
      <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-5">
        <h2 className="mb-4 text-sm font-medium text-neutral-300">Edit plan defaults</h2>

        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Monthly price (¢)">
            <NumInput
              value={patch.monthlyPriceCents}
              current={plan.monthlyPriceCents}
              onChange={(v) => patchField('monthlyPriceCents', v)}
            />
          </Field>
          <Field label="Annual price (¢)">
            <NumInput
              value={patch.annualPriceCents}
              current={plan.annualPriceCents}
              onChange={(v) => patchField('annualPriceCents', v)}
            />
          </Field>
          <Field label="Max members (null = unlimited)">
            <NullableNumInput
              value={patch.maxMembers}
              current={plan.maxMembers}
              onChange={(v) => patchField('maxMembers', v)}
            />
          </Field>
          <Field label="Max projects (null = unlimited)">
            <NullableNumInput
              value={patch.maxProjects}
              current={plan.maxProjects}
              onChange={(v) => patchField('maxProjects', v)}
            />
          </Field>
          <Field label="Storage quota (bytes)">
            <NumInput
              value={patch.storageQuotaBytes}
              current={plan.storageQuotaBytes}
              onChange={(v) => patchField('storageQuotaBytes', v)}
              suffix={`= ${formatBytes(patch.storageQuotaBytes ?? plan.storageQuotaBytes)}`}
            />
          </Field>
          <Field label="Storage per seat (bytes, null = fixed)">
            <NullableNumInput
              value={patch.storageQuotaPerSeatBytes}
              current={plan.storageQuotaPerSeatBytes}
              onChange={(v) => patchField('storageQuotaPerSeatBytes', v)}
              formatter={(n) => `= ${formatBytes(n)}`}
            />
          </Field>
          <Field label="Max file size (bytes)">
            <NumInput
              value={patch.maxFileSizeBytes}
              current={plan.maxFileSizeBytes}
              onChange={(v) => patchField('maxFileSizeBytes', v)}
              suffix={`= ${formatBytes(patch.maxFileSizeBytes ?? plan.maxFileSizeBytes)}`}
            />
          </Field>
          <Field label="Trial days">
            <NumInput
              value={patch.trialDays}
              current={plan.trialDays}
              onChange={(v) => patchField('trialDays', v)}
            />
          </Field>
        </div>

        {/* Features */}
        <div className="mt-5">
          <h3 className="mb-2 text-xs uppercase tracking-wider text-neutral-400">Features</h3>
          <div className="grid gap-2 md:grid-cols-2">
            {FEATURE_KEYS.map((k) => (
              <FeatureToggle
                key={k}
                featureKey={k}
                current={plan.features[k] ?? false}
                pending={patch.features?.[k]}
                onChange={(v) => patchFeature(k, v)}
              />
            ))}
          </div>
        </div>

        {/* Preview + cooldown info */}
        <div className="mt-5 border-t border-neutral-800 pt-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => void runPreview()}
              disabled={!isDirty || previewMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-neutral-800 px-3 py-1.5 text-sm font-medium text-neutral-200 hover:bg-neutral-700 disabled:opacity-40"
            >
              <Wand2 size={14} />
              {previewMut.isPending ? 'Previewing…' : 'Preview impact'}
            </button>
            {!isDirty ? <span className="text-xs text-neutral-500">Edit a field to enable</span> : null}
          </div>

          {preview ? <PreviewBlock preview={preview} /> : null}
        </div>

        {/* Apply form — requires preview + reason + CONFIRM */}
        <div className="mt-5 border-t border-neutral-800 pt-4">
          <h3 className="mb-2 text-sm font-medium text-neutral-300">Apply changes</h3>

          <div className="mb-3">
            <label className="mb-1 block text-xs uppercase tracking-wider text-neutral-400">
              Reason <span className="text-neutral-600">(≥10 chars; recorded in audit log)</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Why this change?  e.g. Raising Free storage to 1 GB based on usage analysis 2026-05-22."
              className={cn(
                'w-full resize-none rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 focus:ring-2 focus:ring-orange-500',
                reasonOk ? 'ring-neutral-700' : 'ring-neutral-700',
              )}
            />
            <div className="mt-1 text-xs text-neutral-500">{reason.trim().length}/10+ chars</div>
          </div>

          <div className="mb-3">
            <label className="mb-1 block text-xs uppercase tracking-wider text-neutral-400">
              Type <code className="text-orange-400">CONFIRM</code> to enable Apply
            </label>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="CONFIRM"
              className="w-full rounded-md bg-neutral-800 px-3 py-2 font-mono text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <button
            onClick={() => void apply()}
            disabled={!canApply}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium text-white',
              canApply ? 'bg-orange-600 hover:bg-orange-700' : 'cursor-not-allowed bg-neutral-800 opacity-50',
            )}
          >
            <Save size={14} />
            {applyMut.isPending ? 'Applying…' : 'Apply changes'}
          </button>

          {!isDirty ? (
            <div className="mt-2 text-xs text-neutral-500">No changes to apply.</div>
          ) : !previewOk && preview === null ? (
            <div className="mt-2 text-xs text-amber-400">Run preview first.</div>
          ) : preview?.cooldown.isInCooldown ? (
            <div className="mt-2 text-xs text-red-400">
              Cooldown active until {new Date(preview.cooldown.cooldownEndsAt ?? '').toLocaleString()}.
            </div>
          ) : null}
        </div>
      </section>

      {/* History */}
      <section className="mt-6 rounded-lg border border-neutral-800 bg-neutral-900 p-5">
        <h2 className="mb-3 text-sm font-medium text-neutral-300">Recent changes</h2>
        {history && history.length > 0 ? (
          <div className="space-y-3">
            {history.map((h) => (
              <HistoryEntry
                key={h.id}
                entry={h}
                pending={revertMut.isPending}
                onRevert={async (reasonText) => {
                  await revertMut.mutateAsync({ code, changeId: h.id, reason: reasonText });
                  toast.success('Reverted');
                }}
              />
            ))}
          </div>
        ) : (
          <p className="text-sm text-neutral-500">No changes yet.</p>
        )}
      </section>

      {/* Hidden state for projected — currently unused on this page; kept for future side-by-side. */}
      {projected ? null : null}
    </AdminLayout>
  );
}

// ── Preview block ─────────────────────────────────────────────────────────

function PreviewBlock({ preview }: { preview: PreviewResult }) {
  return (
    <div className="mt-3 space-y-2 rounded-md bg-neutral-950 p-3 ring-1 ring-neutral-800">
      <div className="flex items-baseline justify-between">
        <div className="text-sm font-medium text-neutral-200">Impact</div>
        <div className="text-xs text-neutral-500">{preview.affectedTeams} teams on this plan</div>
      </div>

      {preview.cooldown.isInCooldown ? (
        <div className="rounded-md bg-red-900/30 px-2 py-1.5 text-xs text-red-300 ring-1 ring-red-900/50">
          <ShieldAlert size={11} className="inline" /> 24h cooldown active.  Try again after{' '}
          {new Date(preview.cooldown.cooldownEndsAt ?? '').toLocaleString()}.
        </div>
      ) : null}

      {preview.warnings.length > 0 ? (
        <div className="space-y-1">
          <div className="text-xs font-medium text-amber-400">Warnings:</div>
          <ul className="ml-4 list-disc space-y-0.5 text-xs text-amber-300">
            {preview.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {preview.teamsExceedingNewLimits.length > 0 ? (
        <details className="rounded bg-red-950/20 p-2 ring-1 ring-red-900/40">
          <summary className="cursor-pointer text-xs text-red-300">
            {preview.teamsExceedingNewLimits.length} teams would exceed new limits (soft-enforced —
            their existing data stays; only new actions blocked) <ChevronDown size={10} className="inline" />
          </summary>
          <ul className="mt-2 max-h-48 overflow-y-auto space-y-1 text-xs text-red-200">
            {preview.teamsExceedingNewLimits.slice(0, 20).map((t) => (
              <li key={t.teamId}>
                <code>/{t.teamSlug}</code> — {t.reasons.join('; ')}
              </li>
            ))}
            {preview.teamsExceedingNewLimits.length > 20 ? (
              <li className="italic text-red-300/70">…and {preview.teamsExceedingNewLimits.length - 20} more</li>
            ) : null}
          </ul>
        </details>
      ) : (
        <div className="text-xs text-emerald-400">All teams within limits.</div>
      )}
    </div>
  );
}

// ── History entry ─────────────────────────────────────────────────────────

function HistoryEntry(props: {
  entry: {
    id: string;
    changedBy: string;
    reason: string;
    isRevert: boolean;
    revertsChangeId: string | null;
    affectedTeamsCount: number | null;
    changedAt: string;
  };
  pending: boolean;
  onRevert: (reason: string) => Promise<void>;
}) {
  const [revertOpen, setRevertOpen] = useState(false);
  const [revertReason, setRevertReason] = useState('');
  const ageDays = (Date.now() - new Date(props.entry.changedAt).getTime()) / 86_400_000;
  const canRevert = ageDays <= 30 && !props.entry.isRevert;

  return (
    <div className="rounded-md bg-neutral-950 p-3 ring-1 ring-neutral-800">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="text-xs text-neutral-500">
            {new Date(props.entry.changedAt).toLocaleString()} ·{' '}
            <code>{props.entry.changedBy.slice(0, 8)}…</code>
            {props.entry.isRevert ? <span className="ml-1 text-orange-400">(revert)</span> : null}
            {props.entry.affectedTeamsCount !== null ? (
              <span className="ml-2">{props.entry.affectedTeamsCount} teams affected</span>
            ) : null}
          </div>
          <div className="mt-1 text-sm text-neutral-200">{props.entry.reason}</div>
        </div>
        {canRevert ? (
          <button
            onClick={() => setRevertOpen(true)}
            disabled={props.pending}
            className="inline-flex items-center gap-1 rounded bg-neutral-800 px-2 py-1 text-xs text-neutral-300 hover:bg-neutral-700"
          >
            <RotateCcw size={11} /> Revert
          </button>
        ) : null}
      </div>

      {revertOpen ? (
        <div className="mt-3 rounded bg-neutral-900 p-3 ring-1 ring-neutral-800">
          <label className="mb-1 block text-xs text-neutral-400">Reason (≥10 chars)</label>
          <textarea
            rows={2}
            value={revertReason}
            onChange={(e) => setRevertReason(e.target.value)}
            placeholder="Why revert?"
            className="w-full resize-none rounded-md bg-neutral-800 px-2 py-1.5 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button onClick={() => setRevertOpen(false)} className="rounded px-2 py-1 text-xs text-neutral-400 hover:bg-neutral-800">
              Cancel
            </button>
            <button
              disabled={revertReason.trim().length < 10 || props.pending}
              onClick={async () => {
                await props.onRevert(revertReason.trim());
                setRevertOpen(false);
                setRevertReason('');
              }}
              className="rounded bg-orange-600 px-2 py-1 text-xs font-medium text-white hover:bg-orange-700 disabled:opacity-50"
            >
              Confirm revert
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ── Form primitives ──────────────────────────────────────────────────────

const FEATURE_KEYS = [
  'github_integration',
  'custom_roles',
  'audit_log',
  'full_reports',
  'outbound_webhooks',
  'public_api',
  'sso',
] as const;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs uppercase tracking-wider text-neutral-400">{label}</label>
      {children}
    </div>
  );
}

function NumInput(props: {
  value: number | undefined;
  current: number;
  onChange: (v: number | undefined) => void;
  suffix?: string;
}) {
  const display = props.value !== undefined ? String(props.value) : '';
  return (
    <div>
      <input
        type="number"
        value={display}
        placeholder={`current: ${props.current}`}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') {
            props.onChange(undefined);
            return;
          }
          const n = Number(raw);
          if (Number.isFinite(n)) props.onChange(n);
        }}
        className="w-full rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
      />
      {props.suffix ? <div className="mt-0.5 text-xs text-neutral-500">{props.suffix}</div> : null}
    </div>
  );
}

function NullableNumInput(props: {
  value: number | null | undefined;
  current: number | null;
  onChange: (v: number | null | undefined) => void;
  formatter?: (n: number) => string;
}) {
  const display =
    props.value === undefined ? '' : props.value === null ? '' : String(props.value);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={display}
          placeholder={
            props.current === null ? 'current: null (unlimited)' : `current: ${props.current}`
          }
          disabled={props.value === null}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              props.onChange(undefined);
              return;
            }
            const n = Number(raw);
            if (Number.isFinite(n)) props.onChange(n);
          }}
          className="flex-1 rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500 disabled:opacity-40"
        />
        <label className="flex items-center gap-1 text-xs text-neutral-400">
          <input
            type="checkbox"
            checked={props.value === null}
            onChange={(e) => props.onChange(e.target.checked ? null : undefined)}
            className="h-3 w-3 rounded border-neutral-600 bg-neutral-800"
          />
          unlimited
        </label>
      </div>
      {props.formatter && typeof props.value === 'number' ? (
        <div className="text-xs text-neutral-500">{props.formatter(props.value)}</div>
      ) : null}
    </div>
  );
}

function FeatureToggle(props: {
  featureKey: string;
  current: boolean;
  pending: boolean | undefined;
  onChange: (v: boolean | null) => void;
}) {
  const effective = props.pending ?? props.current;
  const dirty = props.pending !== undefined && props.pending !== props.current;
  return (
    <div
      className={cn(
        'flex items-center justify-between rounded-md bg-neutral-800 px-3 py-2 ring-1',
        dirty ? 'ring-orange-500/50' : 'ring-neutral-700',
      )}
    >
      <code className="text-sm text-neutral-200">{props.featureKey}</code>
      <div className="flex items-center gap-2">
        {dirty ? (
          <button onClick={() => props.onChange(null)} className="text-xs text-orange-400 hover:text-orange-300">
            reset
          </button>
        ) : null}
        <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-neutral-400">
          <input
            type="checkbox"
            checked={effective}
            onChange={(e) => props.onChange(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-600 bg-neutral-900"
          />
          {effective ? 'enabled' : 'disabled'}
        </label>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(0)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}
